/* eslint-disable react-hooks/set-state-in-effect */
import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const COLLEGIALE_IMG = "/img/lore/collegiale.png";

const SCENES = [
  {
    bg: COLLEGIALE_IMG,
    title: "La Collégiale de Bourg-Palette",
    subtitle: "Aube — Kanto",
    text: (name) =>
      `Le soleil se lève sur le complexe académique édifié à l'est de Bourg-Palette. ` +
      `Vous, ${name}, franchissez le portail pour la première fois en tant que professeur affilié à cette prestigieuse institution.`,
  },
  {
    bg: COLLEGIALE_IMG,
    title: "Le Prof. Chen vous accueille",
    subtitle: "Bureau du fondateur",
    text: (name) =>
      `« Ah, ${name} ! Félicitations pour votre diplôme d'apprenti professeur. ` +
      `J'ai lu vos travaux — vous avez ce qu'il faut pour rejoindre notre grande famille de chercheurs. »`,
  },
  {
    bg: COLLEGIALE_IMG,
    title: "La Collégiale, une académie née du succès",
    subtitle: "Une nouvelle génération",
    text: () =>
      `« Vous savez comme moi que les Pokémon fascinent depuis toujours. Fort de mes années à former les meilleurs dresseurs, ` +
      `j'ai ouvert la Collégiale pour offrir cette même chance à des dizaines de jeunes candidats. ` +
      `Ils se bousculent pour intégrer nos programmes de recherche. »`,
  },
  {
    bg: COLLEGIALE_IMG,
    title: "Votre mission",
    subtitle: "Un voyage initiatique",
    text: () =>
      `« Recrutez ces jeunes talents. Formez-les. Guidez-les à travers Kanto — routes, arènes, forêts. ` +
      `Chaque Pokémon qu'ils captureront viendra enrichir votre propre Pokédex. ` +
      `Conquérir les 8 arènes est essentiel : cela ouvre les zones les plus sauvages, où se cachent les espèces les plus rares. »`,
  },
  {
    bg: COLLEGIALE_IMG,
    title: "Votre dotation de départ",
    subtitle: "500 ₽ · 20 Poké Balls · Kanto",
    text: () =>
      `« Voici de quoi commencer. Une modeste somme, quelques Poké Balls, et l'accès à la région de Kanto. ` +
      `Faites vos preuves : votre renommée grandira avec vos succès, et de nouvelles régions s'ouvriront à vos protégés. »`,
  },
  {
    bg: COLLEGIALE_IMG,
    title: "Prêt à commencer ?",
    subtitle: "Le premier candidat vous attend",
    text: (name) =>
      `« Le premier candidat vous attend dans le hall. Bonne chance, Professeur ${name}. ` +
      `Que votre Pokédex se remplisse et que la Collégiale rayonne à travers Kanto ! »`,
  },
];

export default function IntroCinematic() {
  const { user, refresh } = useAuth();
  const [idx, setIdx] = useState(0);
  const [closing, setClosing] = useState(false);

  const scene = SCENES[idx];
  const name = user?.professor_name || "Professeur";

  const finish = async () => {
    setClosing(true);
    try {
      await api.post("/auth/complete-intro");
      await refresh();
    } catch {
      // fall through — user context will refresh next login
    }
  };

  const next = () => {
    if (idx < SCENES.length - 1) setIdx(idx + 1);
    else finish();
  };
  const prev = () => setIdx(Math.max(0, idx - 1));

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-stretch justify-end"
      style={{ background: "#1A1A1A" }}
      data-testid="intro-cinematic"
    >
      {/* Background illustration */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${scene.bg})`,
          filter: closing ? "brightness(0.3) blur(4px)" : "brightness(0.55)",
          transition: "filter 400ms ease",
        }}
      />
      {/* Skip button */}
      <button
        className="absolute top-4 right-4 z-20 btn-ink text-[11px] bg-[#1A1A1A]/80 text-[#F4EFE6] border-[#F4EFE6]"
        onClick={finish}
        data-testid="intro-skip"
      >
        <X size={12}/> Passer l&apos;intro
      </button>

      {/* Progress dots */}
      <div className="absolute top-4 left-4 z-20 flex gap-1.5" data-testid="intro-dots">
        {SCENES.map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full ${i === idx ? "bg-[#F4EFE6]" : "bg-[#F4EFE6]/30"}`}
          />
        ))}
      </div>

      {/* Text panel — anchored bottom */}
      <div className="relative z-10 bg-[#FCF9F2] border-t-4 border-[#2B2B2B] p-6 md:p-8 max-w-4xl mx-auto w-full mb-0 shadow-2xl">
        <div className="overline text-[10px] text-[#B87333]">{scene.subtitle}</div>
        <h2 className="font-serif-display text-3xl md:text-4xl leading-none mt-1" data-testid="intro-title">
          {scene.title}
        </h2>
        <p className="text-base md:text-lg text-[#1A1A1A] mt-4 leading-relaxed" data-testid="intro-text">
          {scene.text(name)}
        </p>
        <div className="flex justify-between items-center mt-6 flex-wrap gap-3">
          <button
            className="btn-ink text-xs"
            onClick={prev}
            disabled={idx === 0}
            data-testid="intro-prev"
          >
            <ChevronLeft size={12}/> Précédent
          </button>
          <div className="font-mono text-[10px] text-[#7A7A7A]">
            {idx + 1} / {SCENES.length}
          </div>
          <button
            className="btn-ink btn-leaf text-xs"
            onClick={next}
            data-testid="intro-next"
          >
            {idx === SCENES.length - 1 ? "Commencer l'aventure" : "Suivant"} <ChevronRight size={12}/>
          </button>
        </div>
      </div>
    </div>
  );
}
